import {IconButton, TextInput, useTranslationContext} from 'platform/components';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {useDebouncedPageInput} from '../../hooks/useDebouncedPageInput';

type InputModePaginationProps = {
  /** 1-based page (matches the URL/UI space; store `page` is 0-based). */
  page: number;
  pagesQuantity: number;
  onPageChange: (pageOneBased: number) => void;
} & TestIdProps;

/**
 * INPUT-variant page navigation: a free-form page input with prev/next arrows.
 *
 * Composed from platform primitives instead of the platform
 * `Pagination variant="input"` because its InputSegment is controlled and
 * swallows empty values — the user could never delete the current page number
 * before typing a new one. The draft/commit model lives in
 * useDebouncedPageInput: empty drafts are allowed (never committed, snap back
 * on blur), numeric entries are clamped to [1, pagesQuantity] as typed, and
 * commits are debounced so multi-digit pages fetch once.
 *
 * Focusing the input selects its value (TextInput's isAutoselectedOnFocus),
 * so typing replaces the current page.
 */
export function InputModePagination(props: InputModePaginationProps) {
  const t = useTranslationContext();

  const pageInput = useDebouncedPageInput(props.page, props.pagesQuantity, props.onPageChange);

  return (
    <div
      className="flex items-center gap-1"
      data-testid={suffixTestId('inputModePagination', props)}
    >
      <IconButton
        size="small"
        priority="secondary"
        icon="hardware/keyboard_arrow_left"
        isDisabled={pageInput.displayedPage <= 1}
        onClick={pageInput.handlePrevPage}
        data-testid={suffixTestId('paginationPrevButton', props)}
      />
      <div className="w-14">
        <TextInput
          value={pageInput.value}
          onChange={pageInput.handleInputChange}
          onBlur={pageInput.handleInputBlur}
          isAutoselectedOnFocus
          type="number"
          aria-label={t('page.datagrid.labels.pageNumber', {defaultValue: 'Page number'})}
          data-testid={suffixTestId('paginationInput', props)}
        />
      </div>
      <span className="text-[13px] text-[#42526E] whitespace-nowrap">
        {t('general.labels.of', {defaultValue: 'of'})} {props.pagesQuantity}
      </span>
      <IconButton
        size="small"
        priority="secondary"
        icon="hardware/keyboard_arrow_right"
        isDisabled={pageInput.displayedPage >= props.pagesQuantity}
        onClick={pageInput.handleNextPage}
        data-testid={suffixTestId('paginationNextButton', props)}
      />
    </div>
  );
}
